"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import DoctorScrollTabs from "../../../Profile/components/ScrollTabs/DoctorScrollTabs";
import { fetchUserById } from "../../../Profile/redux/thunks/userThunks";
import { fetchMyDocuments, fetchStatistics } from "../../redux/thunks/extractionThunks";
import { resetMyDocuments } from "../../redux/slices/extractionSlice";
import {
  selectMyDocuments,
  selectMyDocumentsLoading,
  selectStatistics,
} from "../../redux/selectors/extractionSelectors";
import { useTour } from "../../../appointments/hooks/useTour";
import { medicalRecordTourSteps } from "./medicalRecordTourConfig";

import MedicalHistory from "../../components/MedicalHistory/DoctorMedicalHistory";
import MedicalRecordUpload from "../../components//MedicalRecordUpload/MedicalRecordUpload";
import RecentRecords from "../../components//RecentRecords/RecentRecords";
import useMedicalCapabilities from "../../../authorization/useMedicalCapabilities";

import "./MedicalRecord.css";

const { Text } = Typography;
const MEDICAL_RECORD_TOUR_ENABLED = false;

export default function DoctorMedicalRecordPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { patientId } = useParams();
  const userId = patientId || null;
  const doctorUser = useSelector((state) => state.doctor?.user || null);
  const doctorId =
    doctorUser?.id || doctorUser?.doctorId || doctorUser?.pgId || doctorUser?._id || null;
  const { capabilities, loading: capabilitiesLoading } =
    useMedicalCapabilities(userId, doctorId);
  const canUpload = Boolean(capabilities?.medicalDocuments?.create);

  const patient = useSelector((state) => state.user?.selectedUser);
  const userLoading = useSelector((state) => state.user?.loading);

  const myDocuments = useSelector(selectMyDocuments);
  const myDocumentsLoading = useSelector(selectMyDocumentsLoading);
  const statistics = useSelector(selectStatistics);

  const [shouldStartTour, setShouldStartTour] = useState(false);

  const onTabChange = (href) => {
    navigate(href);
  };

  useEffect(() => {
    if (!userId) return;
    dispatch(fetchUserById(userId));
  }, [userId, dispatch]);

  useEffect(() => {
    dispatch(resetMyDocuments());
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchMyDocuments({ skip: 0, limit: 50, userId }));
      dispatch(fetchStatistics(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (!MEDICAL_RECORD_TOUR_ENABLED) {
      setShouldStartTour(false);
      return;
    }

    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "medical_record";

    if (hasGuide || hasTour) {
      const timer = setTimeout(() => {
        const mrContent = document.querySelector(".mr-content");
        if (mrContent) {
          setShouldStartTour(true);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }

    setShouldStartTour(false);
  }, [searchParams]);

  useTour(MEDICAL_RECORD_TOUR_ENABLED && shouldStartTour, medicalRecordTourSteps);

  const recentRecords = useMemo(() => {
    if (!myDocuments || myDocuments.length === 0) {
      return [];
    }

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return myDocuments
      .filter((doc) => {
        const docDate = new Date(doc.date_upload || doc.createdAt || Date.now());
        return docDate >= threeMonthsAgo;
      })
      .slice(0, 5)
      .map((doc) => {
        const docDate = new Date(doc.date_upload || doc.createdAt || Date.now());
        const formattedDate = docDate.toLocaleDateString(i18n.language || "en", {
          day: "2-digit",
          month: "long",
        });

        return {
          id: doc._id || doc.id,
          dateLabel: formattedDate,
          title: doc.nom_fichier || doc.type_document || t("medicalRecordd.defaults.documentTitle", "Medical Document"),
          type: doc.type_document,
          pdfUrl: doc.chemin_fichier || doc.file_path,
          fileUrl: doc.chemin_fichier || doc.file_path,
          url: doc.chemin_fichier || doc.file_path,
          onClick: () => {
            const docType = (doc.type_document || "ALL").toUpperCase();
            navigate(`/doctor/patients/${userId}/medicalrecord/${docType}`);
          },
        };
      });
  }, [myDocuments, navigate, t, i18n.language, userId]);

  if (!userId) {
    return (
      <AppLayoutDoctor>
        <div className="mr-center">
          <Alert
            message={t("medicalRecordd.auth.unauthorizedTitle", "Unauthorized Access")}
            description={t(
              "medicalRecordd.auth.unauthorizedDesc",
              "Please log in to access your medical records."
            )}
            type="warning"
            showIcon
          />
        </div>
      </AppLayoutDoctor>
    );
  }

  if (userLoading) {
    return (
      <AppLayoutDoctor>
        <div className="mr-center">
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
            {t("common.loading", "Loading...")}
          </Text>
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <AppLayoutDoctor>
      <div className="mr-page">
        <DoctorScrollTabs patientId={userId} onChange={onTabChange} />

        <div className="mr-content">
          <div className="mr-grid">
            <div className="mr-col mr-col--left">
              <div className="mr-card mr-card--upload">
                {canUpload ? (
                  <MedicalRecordUpload userId={userId} viewMode="doctor" />
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    message={
                      capabilitiesLoading
                        ? t("common.loading", { defaultValue: "Chargement des droits…" })
                        : t("medicalRecord.doctorReadOnly", {
                            defaultValue:
                              "Consultation uniquement : le patient n’a pas autorisé l’ajout de documents.",
                          })
                    }
                  />
                )}
              </div>

              <div className="mr-card mr-card--recent">
                <RecentRecords
                  records={recentRecords}
                  loading={myDocumentsLoading}
                  onFindLab={() => navigate("/labs")}
                  onSelect={undefined}
                  onShare={() => {}}
                  onShareSuccess={() => {}}
                />
              </div>
            </div>

            <div className="mr-col mr-col--right">
              <div className="mr-card mr-card--history">
                <MedicalHistory userId={userId} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayoutDoctor>
  );
}
